import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Breadcrumb } from "../components/Topbar";
import { PageHeader } from "../components/PageHeader";
import { Kpi } from "../components/Kpi";
import { DataTable, type Column } from "../components/DataTable";
import { Badge } from "../components/Badge";
import { useTenants, type TenantSummary } from "../hooks/useTenants";
import { useMrrSummary } from "../hooks/useMrrSummary";
import { formatBRL, formatDate } from "../lib/format";
import { colors, fontSize, fontWeight, space } from "../lib/tokens";
import { PlusIcon } from "../components/icons";

export function HomePage() {
  const navigate = useNavigate();
  const { tenants, loading: tenantsLoading } = useTenants();
  const { data: mrr, loading: mrrLoading } = useMrrSummary();

  const columns: Column<TenantSummary>[] = [
    {
      key: "tenant",
      header: "Tenant",
      render: (r) => (
        <div>
          <div style={{ fontWeight: fontWeight.medium }}>{r.name}</div>
          <div style={{ fontFamily: "JetBrains Mono", fontSize: 11, color: colors.stone, marginTop: 2 }}>{r.slug}</div>
        </div>
      ),
    },
    { key: "state", header: "Estado", render: (r) => <Badge state={r.lifecycle_state} /> },
    { key: "created", header: "Cliente desde", align: "right", render: (r) => <span style={{ color: colors.stone }}>{formatDate(r.created_at)}</span> },
  ];

  return (
    <Layout breadcrumbs={<Breadcrumb items={[{ label: "Visão geral" }]} />}>
      <PageHeader
        title="Visão geral"
        subtitle="Estado da plataforma"
        actions={
          <button
            onClick={() => navigate("/tenants/new")}
            style={{ height: 36, padding: "0 14px", background: colors.paper, color: colors.ink, border: `1px solid ${colors.fog}`, borderRadius: 6, fontSize: fontSize.md, fontWeight: fontWeight.medium, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <PlusIcon width={14} height={14} /> Novo tenant
          </button>
        }
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: space.lg, marginBottom: space.xxxl }}>
        <Kpi
          label="MRR atual"
          value={mrrLoading || !mrr ? "—" : formatBRL(mrr.current_mrr)}
          delta={mrr && mrr.delta_pct !== null ? { direction: mrr.delta_pct > 0 ? "up" : mrr.delta_pct < 0 ? "down" : "flat", text: `${mrr.delta_pct.toFixed(0)}% vs mês -1` } : undefined}
          sub={mrr && mrr.delta_pct === null ? "sem histórico anterior" : undefined}
        />
        <Kpi
          label="Tenants ativos"
          value={mrrLoading || !mrr ? "—" : mrr.active_tenants}
          sub={mrr ? `+${mrr.trial_tenants} trial · +${mrr.prospect_tenants} prospect` : undefined}
        />
        <Kpi
          label="Total membros"
          value={mrrLoading || !mrr ? "—" : mrr.total_members.toLocaleString("pt-BR")}
        />
        <Kpi
          label="Em delinquência"
          value={mrrLoading || !mrr ? "—" : mrr.delinquent_tenants}
          sub={mrr && mrr.delinquent_tenants === 0 ? "Tudo em dia" : undefined}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space.md }}>
        <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold }}>Tenants</div>
        <button onClick={() => navigate("/tenants")} style={{ background: "transparent", border: 0, color: colors.stone, fontSize: fontSize.sm, fontWeight: fontWeight.medium, cursor: "pointer" }}>
          Ver todos →
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={tenants}
        loading={tenantsLoading}
        loadingRows={3}
        onRowClick={(t) => navigate(`/tenants/${t.slug}`)}
      />
    </Layout>
  );
}
